import { useState } from "react";
import usePageMeta from "../hooks/usePageMeta";
import { useNavigate } from "react-router-dom";
import { showError, showSuccess } from "../utils/dialog";
import { getAuthItem } from "../utils/auth";
import { BASE_URL } from "../config";
import "../App.css";
import talentLogo2 from "../assets/talentlogo2.png";

function resolvePostChangeRoute() {
  const userType = String(getAuthItem("userType") || "").toLowerCase();
  const clientId = getAuthItem("clientId");
  const registrationComplete = getAuthItem("registrationComplete") === "true";

  if (userType === "fieldstaff" || userType === "outsourcestaff" || userType === "outsourcedstaff") {
    return registrationComplete ? "/availability" : "/register";
  }
  if (
    clientId &&
    userType !== "fieldstaff" &&
    userType !== "outsourcestaff"
  ) {
    return "/client-dashboard";
  }
  if (!registrationComplete && userType === "candidate") {
    return "/register";
  }
  return "/dashboard";
}

export default function ChangePassword() {
  usePageMeta("Create New Password");
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentPassword.trim()) {
      showError("Current password is required");
      return;
    }
    if (!newPassword || newPassword.length < 8) {
      showError("New password must be at least 8 characters long");
      return;
    }
    if (newPassword !== confirmPassword) {
      showError("Passwords do not match");
      return;
    }
    if (currentPassword === newPassword) {
      showError("New password must be different from your current password");
      return;
    }

    setSaving(true);
    try {
      const token = getAuthItem("token");
      const res = await fetch(`${BASE_URL}/api/my-account/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          currentPassword,
          newPassword,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || "Failed to change password");
      }

      localStorage.removeItem("forcePasswordChange");
      sessionStorage.removeItem("forcePasswordChange");
      showSuccess(data.message || "Password updated successfully");
      setTimeout(() => navigate(resolvePostChangeRoute()), 1200);
    } catch (err) {
      showError(err.message || "Failed to change password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="background-overlay"></div>
      <div className="form-container">
        <div className="form-card">
          <div className="atlas-logo">
            <img src={talentLogo2} alt="Talent Logo" className="h-14 w-auto" />
          </div>

          <h1 className="form-title">Create New Password</h1>
          <p
            style={{
              textAlign: "center",
              fontSize: "0.875rem",
              color: "#6b7280",
              marginBottom: "1rem",
            }}
          >
            Your administrator reset your password. Sign in with that password,
            then choose a new one to continue.
          </p>

          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Current password*</label>
              <div className="password-wrapper">
                <input
                  type={showCurrent ? "text" : "password"}
                  className="form-input"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowCurrent((p) => !p)}
                >
                  {showCurrent ? "🙈" : "👁️"}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">New password*</label>
              <div className="password-wrapper">
                <input
                  type={showNew ? "text" : "password"}
                  className="form-input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  minLength={8}
                  required
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowNew((p) => !p)}
                >
                  {showNew ? "🙈" : "👁️"}
                </button>
              </div>
              <span className="password-hint">Minimum 8 characters</span>
            </div>

            <div className="form-group">
              <label className="form-label">Confirm new password*</label>
              <div className="password-wrapper">
                <input
                  type={showConfirm ? "text" : "password"}
                  className="form-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  minLength={8}
                  required
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowConfirm((p) => !p)}
                >
                  {showConfirm ? "🙈" : "👁️"}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="submit-button"
              style={{ marginTop: "0.5rem", letterSpacing: "0.05em" }}
              disabled={saving}
            >
              {saving ? "Saving..." : "SAVE NEW PASSWORD"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
